import React, { useState } from 'react';
import apiClient from '../../apiClient';
import { API_BASE, USERNAME, PASSWORD } from '../../config';
import DateFilter from '../../Component/common/datefilter/DateFilter';
import Button from '../common/Button/Button';

const AutoFetchLeads = ({ onComplete, setMessage, setIsLoading, apiBase = API_BASE, username = USERNAME, password = PASSWORD }) => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [progress, setProgress] = useState(null);

  // Format date to YYYY-MM-DD
  const formatDate = (date) => {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  const transformTradeIndiaResponse = (data) => {
    if (!data) return [];

    if (Array.isArray(data) && data[0]?.lead_id) {
      return data;
    }

    if (typeof data === 'string' && data.includes('<!DOCTYPE html>')) {
      return [];
    }

    const transformInquiry = (lead) => {
      const contact = lead.contact_details || {};
      return {
        lead_id: lead.lead_id || lead.rfi_id || `temp_${Math.random().toString(36).substr(2, 9)}`,
        contact_name: contact.user_name || lead.sender_name || 'N/A',
        company_name: lead.co_name || lead.sender_co || 'N/A',
        product_name: lead.product_name || 'N/A',
        specifications: lead.specification || '',
        contact_phone: contact.contact_number || lead.sender_mobile || 'N/A',
        contact_email: contact.contact_email || lead.sender_email || 'N/A',
        delivery_location: lead.city || contact.city || lead.sender_city || 'N/A',
        posted_on: lead.posted_on || lead.valid_from || lead.generated_date || new Date().toISOString(),
        responded: lead.user_lead_status || 'New'
      };
    };

    if (Array.isArray(data)) {
      return data.map(transformInquiry);
    }

    if (data?.data?.leads) {
      return data.data.leads.map(transformInquiry);
    }

    if (typeof data === 'object' && data !== null) {
      return [transformInquiry(data)];
    }

    return [];
  };

  const fetchLeadsForDate = async (dateStr) => {
    try {
      // Fetch buy leads and inquiries concurrently
      const buyLeadsPromise = apiClient.post(`${apiBase}/api/get-buy-leads`, {
        username,
        password,
        fromDate: dateStr,
        toDate: dateStr,
        limit: 30000,
        pageNo: 1
      }).then(res => transformTradeIndiaResponse(res.data))
        .catch(err => {
          console.warn(`Buy leads failed for ${dateStr}:`, err.message);
          return [];
        });

      const inquiriesPromise = apiClient.post(`${apiBase}/api/inquiry`, {
        username,
        password,
        fromDate: dateStr,
        toDate: dateStr,
        limit: 1000,
        pageNo: 1
      }).then(res => transformTradeIndiaResponse(res.data))
        .catch(err => {
          console.warn(`Inquiries failed for ${dateStr}:`, err.message);
          return [];
        });

      const [buyLeads, inquiries] = await Promise.all([buyLeadsPromise, inquiriesPromise]);
      return [...buyLeads, ...inquiries];
    } catch (error) {
      console.error(`Error fetching leads for ${dateStr}:`, error);
      return [];
    }
  };

  const handleAutoFetch = async () => {
    if (!fromDate || !toDate) {
      setMessage('Please select both start and end dates');
      return;
    }


    setIsLoading(true);
    setMessage('Starting auto-fetch process...');

    try {
      let startDate = new Date(fromDate);
      let endDate = new Date(toDate);

      // Swap dates if start is after end
      if (startDate > endDate) {
        [startDate, endDate] = [endDate, startDate];
        setFromDate(formatDate(startDate));
        setToDate(formatDate(endDate));
      }

      const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
      let allLeads = [];
      let processedDays = 0;
      let currentDate = new Date(startDate);

      while (currentDate <= endDate) {
        const dateStr = formatDate(currentDate);
        setProgress({
          current: ++processedDays,
          total: totalDays,
          date: dateStr
        });
        setMessage(`Processing ${dateStr} (${processedDays}/${totalDays})`);

        const dailyLeads = await fetchLeadsForDate(dateStr);
        allLeads = [...allLeads, ...dailyLeads];

        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);

        // Add delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Deduplicate all leads by lead_id
      const uniqueLeadsMap = new Map();
      allLeads.forEach(lead => {
        if (lead.lead_id && !uniqueLeadsMap.has(lead.lead_id)) {
          uniqueLeadsMap.set(lead.lead_id, lead);
        }
      });
      const uniqueLeads = Array.from(uniqueLeadsMap.values());

      setMessage(`Auto-fetch completed. Processed ${totalDays} days, found ${uniqueLeads.length} unique leads`);

      onComplete?.();
    } catch (error) {
      console.error('Auto-fetch error:', error);
      setMessage(`Error during auto-fetch: ${error.message}`);
    } finally {
      setIsLoading(false);
      setProgress(null);
    }
  };

  return (
    <div className="auto-fetch-container">
      <h3>Auto-fetch Leads</h3>
      <div className="form-row">
        <DateFilter
          fromDate={fromDate}
          toDate={toDate}
          setFromDate={setFromDate}
          setToDate={setToDate}
        />
        <Button
        onClick={handleAutoFetch}
        disabled={!fromDate || !toDate}
        className="btn fetch-button"
      >
        Fetch and Store Leads
      </Button>
      </div>


      {progress && (
        <div className="progress-info">
          <p>Processing day {progress.current} of {progress.total}</p>
          <p>Current date: {progress.date}</p>
          <progress value={progress.current} max={progress.total} />
        </div>
      )}
    </div>
  );
};

export default AutoFetchLeads;